import { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { useSettings, useUpdateSettings } from '@/api/settings';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';

import { extractErrorMessage } from '@/lib/extractErrorMessage';
/*
 * Configuration → Scheduling → Rules. Practice-wide scheduling behavior:
 * EVV enablement and whether sessions block when the assigned staff has an
 * expired credential. Lands in settings.scheduling.
 */

interface FormState {
    evvEnabled: boolean;
    blockCertExpired: boolean;
}

const EMPTY: FormState = {
    evvEnabled: true,
    blockCertExpired: true,
};

export function SchedulingRulesPanel() {
    const { data, isLoading, isError } = useSettings();
    const updateM = useUpdateSettings();

    const [form, setForm] = useState<FormState>(EMPTY);
    const [err, setErr] = useState<string | null>(null);
    const [saved, setSaved] = useState(false);

    useEffect(() => {
        if (!data?.scheduling) return;
        const s = data.scheduling;
        setForm({
            evvEnabled:       s.evvEnabled ?? true,
            blockCertExpired: s.blockCertExpired ?? true,
        });
    }, [data]);

    function set<K extends keyof FormState>(k: K, v: FormState[K]) {
        setSaved(false);
        setErr(null);
        setForm((f) => ({ ...f, [k]: v }));
    }

    async function handleSubmit(e: React.FormEvent) {
        e.preventDefault();
        setErr(null);
        setSaved(false);

        try {
            await updateM.mutateAsync({
                type: 'scheduling',
                updates: {
                    evvEnabled: form.evvEnabled,
                    blockCertExpired: form.blockCertExpired,
                },
            });
            setSaved(true);
        } catch (e2) {
            setErr(extractErrorMessage(e2, 'Could not save.'));
        }
    }

    if (isLoading) {
        return (
            <div className="p-12 flex items-center justify-center text-text-subtle">
                <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading…
            </div>
        );
    }
    if (isError) {
        return <div className="p-12 text-center text-error">Couldn't load settings.</div>;
    }

    return (
        <div>
            <div className="mb-1">
                <h2 className="font-display text-lg font-bold text-text">Scheduling Rules</h2>
            </div>
            <p className="text-xs text-text-subtle mb-3">
                Practice-wide scheduling behavior. EVV controls visibility of the
                clock-in/out surface; the credential block prevents assigning
                sessions to staff with expired credentials.
            </p>

            <Card className="p-5 max-w-3xl">
                <form onSubmit={handleSubmit}>
                    <label className="flex items-start gap-2 mb-4 text-sm text-text cursor-pointer">
                        <input
                            type="checkbox"
                            checked={form.evvEnabled}
                            onChange={(e) => set('evvEnabled', e.target.checked)}
                            className="h-4 w-4 mt-0.5"
                        />
                        <span>
                            <span className="font-medium">Visit Verification</span>
                            <span className="block text-xs text-text-subtle">
                                Surfaces check-in / check-out controls on sessions and the visit
                                verification report. Captures GPS and timestamps for internal
                                audit, billing-dispute defense, and payroll verification.
                            </span>
                            <span className="block text-xs text-warning-text mt-1">
                                Not a substitute for state-mandated EVV. If Medicaid in your state
                                requires ABA visits to transmit to a designated aggregator
                                (Sandata, HHAeXchange, Tellus, etc.), you'll still need that
                                aggregator's EVV app or a certified Alt-EVV vendor — this system
                                does not transmit to aggregators.
                            </span>
                        </span>
                    </label>

                    <label className="flex items-start gap-2 mb-4 text-sm text-text cursor-pointer">
                        <input
                            type="checkbox"
                            checked={form.blockCertExpired}
                            onChange={(e) => set('blockCertExpired', e.target.checked)}
                            className="h-4 w-4 mt-0.5"
                        />
                        <span>
                            <span className="font-medium">Block scheduling when staff credential is expired</span>
                            <span className="block text-xs text-text-subtle">
                                Prevents assigning a session to a staff member whose BACB or other
                                credential has lapsed. Leave off to allow the save with a warning.
                            </span>
                        </span>
                    </label>

                    {err && <p className="text-sm text-error mb-2">{err}</p>}
                    {saved && <p className="text-sm text-success mb-2">Saved.</p>}

                    <div className="flex justify-end">
                        <Button variant="primary" size="sm" type="submit" disabled={updateM.isPending}>
                            {updateM.isPending ? 'Saving…' : 'Save'}
                        </Button>
                    </div>
                </form>
            </Card>
        </div>
    );
}
